import { LockKeyhole, Mail, Phone, Sparkles, User } from '../components/icons';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import ThemeToggle from '../components/ThemeToggle';
import { authService } from '../services/authService';
import { useAuth } from '../store/authContext';
import { useToast } from '../store/toastContext';
import { formatPhoneMask, isValidPhone, normalizePhone } from '../utils/phone';

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EyeIcon = ({ off = false }: { off?: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
    <circle cx="12" cy="12" r="3" />
    {off ? <path d="M4 4l16 16" /> : null}
  </svg>
);

const toPtError = (err: unknown) => {
  const msg = err instanceof Error ? err.message : 'Erro inesperado.';
  const lower = msg.toLowerCase();
  if (lower.includes('failed to fetch') || lower.includes('network')) return 'Falha de conexão com o servidor.';
  return msg;
};

export default function RegisterPage() {
  const { register, loading } = useAuth();
  const { pushToast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const canSubmit = useMemo(() => !loading, [loading]);

  const validate = () => {
    const next: FormErrors = {};

    if (name.trim().length < 3) next.name = 'Informe nome completo.';
    if (!emailRegex.test(email)) next.email = 'Email inválido.';
    if (!isValidPhone(phone)) next.phone = 'Use telefone BR/PT válido.';
    if (password.length < 8) next.password = 'Senha com mínimo de 8 caracteres.';
    if (confirmPassword !== password) next.confirmPassword = 'Senhas não conferem.';

    setErrors(next);
    if (Object.keys(next).length > 0) {
      const firstError = Object.values(next)[0];
      if (firstError) pushToast({ type: 'error', title: firstError });
    }
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const normalized = normalizePhone(phone);
    if (!normalized) {
      const msg = 'Número inválido.';
      setErrors((prev) => ({ ...prev, phone: msg }));
      pushToast({ type: 'error', title: msg });
      return;
    }

    try {
      await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: normalized,
        password,
        confirmPassword
      });
      const session = authService.getSession();
      pushToast({
        type: 'success',
        title: `Bem-vindo, ${session?.user.name || name.trim()}`,
        description: 'Cadastro concluído com sucesso.'
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = toPtError(err);
      pushToast({ type: 'error', title: 'Erro no cadastro', description: msg });
    }
  };

  return (
    <div className="min-h-screen bg-bgLight px-4 py-8 dark:bg-bgDark">
      <div className="mx-auto max-w-xl rounded-3xl border border-borderLight bg-cardLight p-6 shadow-xl dark:border-borderDark dark:bg-cardDark">
        <div className="mb-5 flex items-center justify-between">
          <Link to="/" className="text-sm font-semibold text-primary dark:text-cta">
            Voltar
          </Link>
          <ThemeToggle />
        </div>

        <div>
          <h1 className="text-2xl font-bold">Criar conta</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Configure seu ambiente e comece sua operação.</p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <fieldset disabled={loading} className="space-y-4">
              <Input label="Nome" value={name} onChange={(e) => setName(e.target.value)} error={errors.name} iconLeft={<User className="h-4 w-4" />} />
              <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} iconLeft={<Mail className="h-4 w-4" />} />
              <Input
                label="Telefone BR/PT"
                value={phone}
                onChange={(e) => setPhone(formatPhoneMask(e.target.value))}
                error={errors.phone}
                iconLeft={<Phone className="h-4 w-4" />}
              />
              <Input
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                iconLeft={<LockKeyhole className="h-4 w-4" />}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-300"
              >
                <EyeIcon off={showPassword} />
                {showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              </button>
              <Input
                label="Confirmar senha"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={errors.confirmPassword}
                iconLeft={<LockKeyhole className="h-4 w-4" />}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-300"
              >
                <EyeIcon off={showConfirmPassword} />
                {showConfirmPassword ? 'Ocultar confirmação' : 'Mostrar confirmação'}
              </button>

              <Button variant="cta" fullWidth disabled={!canSubmit}>
                {loading ? 'Criando conta...' : 'Criar conta'}
              </Button>
            </fieldset>
          </form>
        </div>
      </div>

      {loading ? (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-slate-900/55 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-sm rounded-2xl border border-white/25 bg-white/95 p-6 text-center shadow-2xl dark:border-white/15 dark:bg-slate-900/95">
            <div className="mx-auto mb-3 inline-flex h-10 w-10 animate-pulse items-center justify-center rounded-xl bg-primary/15 text-primary dark:bg-cta/20 dark:text-cta">
              <Sparkles className="h-5 w-5" />
            </div>
            <p className="text-sm font-semibold">Criando sua conta...</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">Estamos validando seus dados e finalizando o cadastro.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
